// Features: M004-F0730, M004-F0731, M004-F0732, M004-F0772
// Tests: T-M004-0255, T-M004-0256, T-M004-0257, T-M004-0258, T-M004-0259, T-M004-0372
// Design: docs/design/M004-F0730-F0732-Billing-Settings-Page.md
//
// The organisation's invoice and billing settings on studioaeye.com. UI flow standard: Ledger home (nav) -> this page -> click a TAG
// -> side DRAWER opens on that field -> edit -> Save. No stacked modals: the prefix confirmation, the logo removal and the discard
// guard are inline panels INSIDE the drawer. The page renders what Core/Ledger give it, mirrors a rule only for typing feedback
// (the Ledger is the judge) and has no app_code branch. No SMTP/secret field exists. Bank details live in component state only.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Layout from './layout/Layout';
import StepUpDialog from './auth/StepUpDialog';
import CreditPolicyCard from './ledger/CreditPolicyCard';
import { ledgerAccountApi, organisationApi } from '../services/api';
import { getStepUpChallengeFromError } from '../utils/stepUp';
import { classifyRelayOutcome, submittedFieldsMatch } from '../services/relayOutcome';
import {
  DENIED_SENTENCE, FIELD_FOR_CODE, LOGO_SENTENCE, NOT_UNDERSTOOD, READ_CONDITIONS, STALE_SENTENCE,
  errorCode, errorDetails, errorMessage,
} from '../services/billingProfileAdapter';
import {
  ADDRESS_MAX_LINES, FIELD_NAMES, LOGO_MAX_BYTES, PAD_WIDTH, buildChanges, effectiveDueDays, fieldProblems, isBlank,
  nextInvoiceNumber, padSeq, profileToForm, termsMismatch,
} from '../utils/billingProfileRules';
import './LedgerBillingSettingsPage.css';

export const LABELS = Object.freeze({
  issuer_name: 'Issuer name',
  issuer_address: 'Issuer address',
  vat_number: 'VAT number',
  bank_details: 'Bank details',
  payment_terms_text: 'Payment terms text',
  invoice_prefix: 'Invoice prefix',
  invoice_due_days: 'Payment due (days)',
  bookkeeper_email: 'Bookkeeper email',
  support_email: 'Support email',
  logo: 'Logo',
});
const TAG_ORDER = [...FIELD_NAMES, 'logo'];
const MISSING_CODES = ['issuer_name_missing', 'issuer_address_missing', 'bank_details_missing', 'payment_terms_missing', 'invoice_prefix_missing'];
const newKey = () => globalThis.crypto?.randomUUID?.() || `bp-${Date.now()}-${Math.random()}`;
// M004-F0772 (WebUI #245): the save outcome when Core's relay answer was lost or late and the Ledger may have applied the save.
export const SLOW_SAVED_SENTENCE = 'Saved (confirmation was slow).';
export const NOT_SAVED_SENTENCE = 'Not saved — please retry.';
export const UNCONFIRMED_SENTENCE = "We couldn't confirm whether this was saved. Reload to check.";
const logoSentence = (logo) => (logo?.exists ? `Logo uploaded, ${logo.width} x ${logo.height} px, ${Math.round((logo.bytes || 0) / 1024)} KB` : 'No logo uploaded');

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}
const readFileBuffer = (file) => (file.arrayBuffer ? file.arrayBuffer() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(file);
}));

const summaryValue = (name, profile) => {
  const v = profile[name];
  if (name === 'bank_details') return v == null ? null : `Set (${v.length} characters)`;
  if (name === 'issuer_address') return v == null ? null : v.join(', ');
  if (name === 'invoice_due_days') return v == null ? null : `${v} days`;
  return v == null || v === '' ? null : String(v);
};

// Applies the user's own wire-level changes back onto a form built from the freshly read profile.
const formWithChanges = (profile, changes) => profileToForm({ ...profile, ...changes });

export default function LedgerBillingSettingsPage() {
  const { orgId } = useParams();
  const location = useLocation();
  // load: loading | ready | denied | condition | notUnderstood
  const [load, setLoad] = useState({ status: 'loading', message: '' });
  const [data, setData] = useState(null); // the validated BillingProfile as last read or saved
  const [form, setForm] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [drawer, setDrawer] = useState(null); // null or { focus }
  const [logoPending, setLogoPending] = useState(null); // { action:'replace', base64, name, bytes } | { action:'remove' }
  const [logoConfirmRemove, setLogoConfirmRemove] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // from the Ledger, by field
  const [serverProblems, setServerProblems] = useState({});
  const [summaryError, setSummaryError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { oldPrefix, newPrefix, seq, firstNumber, count, reason, error }
  const [discardAsk, setDiscardAsk] = useState(false);
  const [stale, setStale] = useState(null); // { conflicts: { field: savedWireValue } }
  const [partial, setPartial] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [announce, setAnnounce] = useState('');
  const attempt = useRef(null); // { signature, key } so a retry of the SAME action re-sends the SAME Idempotency-Key
  const pendingSave = useRef(null);
  const firstFieldRef = useRef({});
  const requestId = useRef(0);

  const readProfile = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoad({ status: 'loading', message: '' });
    try {
      const profile = await ledgerAccountApi.getBillingProfile(orgId);
      if (requestId.current !== id) return null;
      setData(profile);
      setLoad({ status: 'ready', message: '' });
      return profile;
    } catch (cause) {
      if (requestId.current !== id) return null;
      const code = errorCode(cause);
      const status = Number(cause?.response?.status);
      setData(null); setForm(null);
      if (cause?.code === NOT_UNDERSTOOD) setLoad({ status: 'notUnderstood', message: 'The settings answer was not understood.' });
      else if (status === 403) setLoad({ status: 'denied', message: DENIED_SENTENCE });
      else if (READ_CONDITIONS[code]) setLoad({ status: 'condition', message: READ_CONDITIONS[code] });
      // Core's own routing-layer refusal for a route it does not serve yet (not a declared contract code).
      else if (status === 404 && code === 'NOT_FOUND') setLoad({ status: 'condition', message: READ_CONDITIONS.LEDGER_ACCOUNT_OPERATION_UNSUPPORTED });
      else setLoad({ status: 'condition', message: errorMessage(cause) || 'The billing settings could not be loaded.' });
      return null;
    }
  }, [orgId]);

  useEffect(() => { readProfile().then((profile) => { if (profile) setForm(profileToForm(profile.profile)); }); }, [readProfile]);
  useEffect(() => {
    let current = true;
    organisationApi.getMine().then((response) => {
      const body = response?.data?.data || response?.data || [];
      const list = Array.isArray(body) ? body : body.organisations || body.items || [];
      const found = list.find((o) => String(o.id || o.organisation_id) === String(orgId));
      if (current) setOrganisation(found || null);
    }).catch(() => {});
    return () => { current = false; };
  }, [orgId]);

  // The readiness list of the Approval card links here with #<field>: open the drawer on that field once loaded.
  useEffect(() => {
    const target = (location.hash || '').replace('#', '');
    if (load.status === 'ready' && form && TAG_ORDER.includes(target)) setDrawer((d) => d || { focus: target });
  }, [load.status, location.hash]);

  useEffect(() => {
    if (drawer?.focus) {
      const el = firstFieldRef.current[drawer.focus];
      if (el && el.focus) el.focus();
    }
  }, [drawer]);

  const loadedProfile = data?.profile;
  const dirtyFields = useMemo(() => (form && loadedProfile ? Object.keys(buildChanges(form, loadedProfile)) : []), [form, loadedProfile]);
  const dirty = dirtyFields.length > 0 || Boolean(logoPending);
  const problems = useMemo(() => (form && loadedProfile ? fieldProblems(form, loadedProfile) : {}), [form, loadedProfile]);
  const hasProblem = Object.keys(problems).length > 0;

  useEffect(() => {
    if (!dirty) return undefined;
    const guard = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  // Live next number and live terms warning: computed locally on every keystroke, no request.
  const next = form && data ? nextInvoiceNumber({
    typedPrefix: form.invoice_prefix, loadedPrefix: loadedProfile.invoice_prefix, orgCode: data.context.org_code, nextSeq: data.numbering.next_seq, padWidth: data.numbering.pad_width || PAD_WIDTH,
  }) : null;
  const nextText = next ? (next.invalid ? 'Next invoice number: (fix the prefix)' : `Next invoice number: ${next.text}`) : '';
  useEffect(() => {
    const t = setTimeout(() => setAnnounce(nextText), 500);
    return () => clearTimeout(t);
  }, [nextText]);
  const liveMismatch = form ? termsMismatch(isBlank(form.payment_terms_text) ? null : form.payment_terms_text, isBlank(form.invoice_due_days) || problems.invoice_due_days ? null : Number(form.invoice_due_days)) : null;

  const setField = (name, value) => { setForm((f) => ({ ...f, [name]: value })); setFieldErrors((e) => ({ ...e, [name]: undefined })); setServerProblems((p) => ({ ...p, [name]: undefined })); setSummaryError(''); };
  const setAddressLine = (i, value) => setField('issuer_address', form.issuer_address.map((l, j) => (j === i ? value : l)));
  const addAddressLine = () => form.issuer_address.length < ADDRESS_MAX_LINES && setField('issuer_address', [...form.issuer_address, '']);
  const removeAddressLine = (i) => setField('issuer_address', form.issuer_address.length > 1 ? form.issuer_address.filter((_, j) => j !== i) : ['']);

  const openDrawer = (focus) => { setDrawer({ focus }); setNotice(''); setDiscardAsk(false); };
  const reset = () => {
    if (!data) return;
    setForm(profileToForm(data.profile)); setLogoPending(null); setLogoConfirmRemove(false); setFieldErrors({}); setSummaryError(''); setStale(null); setPartial(''); setConfirm(null);
  };
  const closeDrawer = () => {
    if (dirty && !discardAsk) { setDiscardAsk(true); return; }
    reset(); setDrawer(null); setDiscardAsk(false);
  };

  const chooseLogo = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.type !== 'image/jpeg' || file.size > LOGO_MAX_BYTES || file.size === 0) { setFieldErrors((e) => ({ ...e, logo: LOGO_SENTENCE })); return; }
    try {
      const base64 = toBase64(await readFileBuffer(file));
      setLogoPending({ action: 'replace', base64, name: file.name, bytes: file.size });
      setLogoConfirmRemove(false);
      setFieldErrors((e) => ({ ...e, logo: undefined }));
    } catch (_error) { setFieldErrors((e) => ({ ...e, logo: LOGO_SENTENCE })); }
  };

  // ---- save ---------------------------------------------------------------------------------------------------
  const applyAnswer = useCallback((result) => {
    const profile = result.billing_profile;
    setData(profile);
    setForm(profileToForm(profile.profile));
    setLogoPending(null); setLogoConfirmRemove(false); setFieldErrors({}); setServerProblems({}); setSummaryError(''); setStale(null); setPartial(''); setConfirm(null); setDiscardAsk(false);
    const names = [...result.changed.map((n) => LABELS[n]), ...(result.logo_changed ? ['Logo'] : [])];
    setNotice(names.length ? `Saved: ${names.join(', ')}.` : 'No changes.');
  }, []);

  // M004-F0772: an unknown outcome is re-read and compared on the fields this save SUBMITTED. Never a definite failure.
  // The idempotency key is dropped only once the save is confirmed: an unconfirmed retry of the same change re-sends the same key.
  const confirmUnknownSave = useCallback(async (payload) => {
    let fresh = null;
    try { fresh = await ledgerAccountApi.getBillingProfile(orgId); } catch (_cause) { fresh = null; }
    if (!fresh) return UNCONFIRMED_SENTENCE;
    if (submittedFieldsMatch(fresh, payload)) {
      attempt.current = null;
      setData(fresh);
      setForm(profileToForm(fresh.profile));
      setLogoPending(null); setLogoConfirmRemove(false); setFieldErrors({}); setServerProblems({}); setSummaryError(''); setStale(null); setPartial(''); setConfirm(null); setDiscardAsk(false);
      setNotice(SLOW_SAVED_SENTENCE);
      return '';
    }
    // Not saved: keep the user's edits (as the stale path does) and adopt the fresh version so a retry is against current state.
    const mine = buildChanges(form, data.profile);
    setData(fresh);
    setForm(formWithChanges(fresh.profile, mine));
    return NOT_SAVED_SENTENCE;
  }, [orgId, form, data]);

  const runSave = useCallback(async (payload, key) => {
    setSaving(true); setSummaryError(''); setPartial('');
    try {
      const result = await ledgerAccountApi.updateBillingProfile(orgId, payload, key);
      pendingSave.current = null; attempt.current = null;
      applyAnswer(result);
      return true;
    } catch (cause) {
      const status = Number(cause?.response?.status);
      const code = errorCode(cause);
      const details = errorDetails(cause);
      // Step-up is Core's 401 challenge; a 403 is a refusal (never opens the dialog).
      const challenge = status === 403 ? null : getStepUpChallengeFromError(cause);
      if (challenge?.id) {
        pendingSave.current = { payload, key };
        setStepUpChallenge(challenge);
      } else if (cause?.code === NOT_UNDERSTOOD) {
        setSummaryError('The settings answer was not understood. Reload to see what is saved.');
      } else if (classifyRelayOutcome(cause) === 'unknown') {
        setSummaryError(await confirmUnknownSave(payload));
      } else if (status === 409 && code === 'LEDGER_BILLING_PROFILE_STALE') {
        attempt.current = null;
        const old = data.profile;
        const mine = buildChanges(form, old);
        const fresh = await readProfile();
        if (fresh) {
          const conflicts = {};
          Object.keys(mine).forEach((f) => { if (JSON.stringify(fresh.profile[f] ?? null) !== JSON.stringify(old[f] ?? null)) conflicts[f] = fresh.profile[f] ?? null; });
          setForm(formWithChanges(fresh.profile, mine));
          setStale({ conflicts });
        } else setStale({ conflicts: {} });
        setSummaryError(STALE_SENTENCE);
      } else if (status === 409 && code === 'LEDGER_INVOICE_PREFIX_CONFIRM_REQUIRED') {
        setConfirm({
          oldPrefix: data.profile.invoice_prefix ?? data.effective.invoice_prefix, newPrefix: form.invoice_prefix,
          firstNumber: details.first_invoice_number, count: details.issued_invoice_count, nextAfter: details.next_invoice_number_after, reason: '', error: '',
        });
      } else if (status === 500 && code === 'LEDGER_BILLING_PROFILE_PARTIAL') {
        const applied = Array.isArray(details.applied) ? details.applied.map((n) => LABELS[n] || n).join(', ') : String(details.applied || '');
        setPartial(`Some changes were saved: ${applied}. The logo was not.`);
        await readProfile();
      } else if (status === 403 && code === 'LEDGER_ACCOUNT_PERMISSION_DENIED') {
        setSummaryError(DENIED_SENTENCE);
      } else if (FIELD_FOR_CODE[code] || code === 'LEDGER_BILLING_PROFILE_INVALID' || code === 'LEDGER_BILLING_PROFILE_FIELD_NOT_EDITABLE') {
        const field = FIELD_FOR_CODE[code] || (FIELD_NAMES.includes(details.field) ? details.field : '');
        const sentence = code.startsWith('LEDGER_LOGO') ? LOGO_SENTENCE : (errorMessage(cause) || 'The Ledger refused this value.');
        if (field === 'reason') setConfirm((c) => (c ? { ...c, error: sentence } : c));
        else if (field) setServerProblems((p) => ({ ...p, [field]: sentence }));
        setSummaryError(sentence);
      } else {
        setSummaryError(errorMessage(cause) || 'The settings could not be saved.');
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [orgId, applyAnswer, data, form, readProfile, confirmUnknownSave]);

  const startSave = useCallback((confirmation) => {
    const changes = buildChanges(form, loadedProfile);
    const payload = {
      expectedVersion: data.version, changes,
      logo: logoPending ? (logoPending.action === 'replace' ? { action: 'replace', jpeg_base64: logoPending.base64 } : { action: 'remove' }) : undefined,
      confirmPrefixChange: Boolean(confirmation), reason: confirmation ? confirmation.reason.trim() : undefined,
    };
    // One Idempotency-Key per user action; a retry of the SAME action (same body) re-sends the SAME key.
    const signature = JSON.stringify([payload.expectedVersion, payload.changes, payload.logo ? payload.logo.action + (payload.logo.jpeg_base64 || '').length : null, payload.confirmPrefixChange, payload.reason]);
    if (!attempt.current || attempt.current.signature !== signature) attempt.current = { signature, key: newKey() };
    return runSave(payload, attempt.current.key);
  }, [form, loadedProfile, data, logoPending, runSave]);

  const submit = (event) => {
    event.preventDefault();
    if (saving || !form) return;
    if (hasProblem) return;
    const changes = buildChanges(form, loadedProfile);
    if (!dirty) { setNotice('No changes.'); return; }
    if ('invoice_prefix' in changes && data.numbering.prefix_change_requires_confirmation) {
      setConfirm({
        oldPrefix: loadedProfile.invoice_prefix ?? data.effective.invoice_prefix, newPrefix: form.invoice_prefix ?? '',
        firstNumber: data.numbering.first_invoice_number, count: data.numbering.issued_invoice_count, nextAfter: next && !next.invalid ? next.text : '', reason: '', error: '',
      });
      return;
    }
    startSave(null);
  };

  const confirmChange = (event) => {
    event.preventDefault();
    const reason = (confirm.reason || '').trim();
    if (reason.length < 3 || reason.length > 500) { setConfirm((c) => ({ ...c, error: 'Give a reason of 3 to 500 characters.' })); return; }
    startSave({ reason });
  };

  const confirmRef = useRef(null);
  useEffect(() => { if (confirm && confirmRef.current) confirmRef.current.focus(); }, [!!confirm]);

  // ---- render ---------------------------------------------------------------------------------------------------
  const orgName = organisation?.name || organisation?.organisation_name || `Organisation ${orgId}`;
  const heading = (
    <header className="bps-heading">
      <Link to="/app/ledger">← Ledger home</Link>
      <span>{orgName}</span>
      <h1>Billing settings</h1>
      <p>The issuer details, bank details, payment terms and invoice numbering printed on every invoice.</p>
    </header>
  );

  if (load.status === 'loading' && !data) {
    return <Layout><div className="bps-page" aria-busy="true">{heading}<div className="bps-skeleton" role="status">Loading billing settings…</div></div></Layout>;
  }
  if (load.status === 'denied') {
    return <Layout><div className="bps-page">{heading}<p className="bps-denied" role="alert" data-testid="billing-settings-denied">{DENIED_SENTENCE}</p></div></Layout>;
  }
  if (load.status === 'condition' || load.status === 'notUnderstood' || !data || !form) {
    return (
      <Layout><div className="bps-page">{heading}
        <section className="bps-condition" data-testid="billing-settings-condition" role="alert">
          <p>{load.message || 'The billing settings could not be loaded.'}</p>
          <button type="button" className="bps-secondary" onClick={() => readProfile().then((p) => p && setForm(profileToForm(p.profile)))}>Retry</button>
        </section>
      </div></Layout>
    );
  }

  const profile = data.profile;
  const { context, numbering } = data;
  const missing = data.warnings.filter((w) => MISSING_CODES.includes(w.code));
  const otherWarnings = data.warnings.filter((w) => !MISSING_CODES.includes(w.code));
  const err = (name) => problems[name] || serverProblems[name] || fieldErrors[name];
  const describedBy = (name) => [`bps-hint-${name}`, err(name) ? `bps-err-${name}` : null, stale?.conflicts && name in stale.conflicts ? `bps-saved-${name}` : null].filter(Boolean).join(' ');
  const savedBeside = (name) => (stale && name in stale.conflicts ? (
    <p className="bps-saved-beside" id={`bps-saved-${name}`} data-testid={`billing-saved-${name}`}>
      Saved now: {name === 'issuer_address' ? (stale.conflicts[name] || []).join(', ') || 'not set' : (stale.conflicts[name] == null ? 'not set' : String(stale.conflicts[name]))}
    </p>
  ) : null);
  const inputProps = (name) => ({
    id: `bps-${name}`, ref: (el) => { firstFieldRef.current[name] = el; }, 'aria-describedby': describedBy(name), 'aria-invalid': err(name) ? 'true' : undefined,
  });
  const fieldBlock = (name, hint, control) => (
    <div className="bps-field" key={name}>
      <label htmlFor={`bps-${name}`}>{LABELS[name]}</label>
      {control}
      <p className="bps-hint" id={`bps-hint-${name}`}>{hint}</p>
      <p className="bps-error" id={`bps-err-${name}`} role={err(name) ? 'alert' : undefined} data-testid={`billing-error-${name}`}>{err(name) || ''}</p>
      {savedBeside(name)}
    </div>
  );
  const noValue = <span className="bps-unset">Not set</span>;

  return (
    <Layout>
      <StepUpDialog
        challenge={stepUpChallenge}
        title="Verify billing settings change"
        description="Bank details are sensitive: confirm your identity before saving."
        onComplete={() => { const p = pendingSave.current; setStepUpChallenge(null); if (p) runSave(p.payload, p.key); }}
        onCancel={() => { pendingSave.current = null; setStepUpChallenge(null); setSummaryError('Verification was cancelled. Your edits are kept.'); }}
      />
      <div className="bps-page" data-testid="billing-settings-page">
        {heading}
        <p className="bps-context" data-testid="billing-context">
          Currency {context.currency} · VAT registered: {context.vat_registered ? 'yes' : 'no'} · Organisation code {context.org_code}
        </p>
        {notice ? <p className="bps-notice" role="status" data-testid="billing-notice">{notice}</p> : null}
        {partial ? <p className="bps-warning-block" role="alert" data-testid="billing-partial">{partial}</p> : null}

        {missing.length ? (
          <section className="bps-warnings" aria-labelledby="bps-needed" data-testid="billing-warnings-needed">
            <h2 id="bps-needed">Needed before an invoice can be issued</h2>
            <ul>{missing.map((w) => <li key={w.code} className="bps-warning">Warning: {w.message}</li>)}</ul>
          </section>
        ) : null}
        {otherWarnings.length ? (
          <section className="bps-warnings" aria-label="Warnings" data-testid="billing-warnings">
            <ul>{otherWarnings.map((w) => <li key={w.code} className="bps-warning">Warning: {w.message}</li>)}</ul>
          </section>
        ) : null}

        <ul className="bps-tags" aria-label="Billing settings">
          {TAG_ORDER.map((name) => (
            <li key={name}>
              <button type="button" className="bps-tag" data-testid={`billing-tag-${name}`} onClick={() => openDrawer(name)}>
                <span className="bps-tag-label">{LABELS[name]}</span>
                <span className="bps-tag-value">
                  {name === 'logo' ? logoSentence(data.logo)
                    : name === 'invoice_prefix' ? (profile.invoice_prefix ? `${profile.invoice_prefix}  ·  Next: ${numbering.next_invoice_number}` : `Default (${data.effective.invoice_prefix})  ·  Next: ${numbering.next_invoice_number}`)
                      : name === 'invoice_due_days' ? (summaryValue(name, profile) || `Default (${effectiveDueDays(null)} days)`)
                        : (summaryValue(name, profile) || noValue)}
                </span>
                <span aria-hidden="true" className="bps-tag-go">Edit</span>
              </button>
            </li>
          ))}
        </ul>
        {/* ML001-F0219 (WebUI #170): the organisation's credit application default. Billing settings are ledger_manage only, so the card is editable here. */}
        <CreditPolicyCard level="org" title="Credit application (organisation default)" orgId={orgId} canManage />
        <p className="bps-audit" data-testid="billing-audit-pointer">
          Every save is recorded, including who made it.{data.updated_at ? ` Last saved ${new Date(data.updated_at).toLocaleString('en-GB')}.` : ' Not saved yet.'}
        </p>

        {drawer ? (
          <>
            <div className="bps-backdrop" aria-hidden="true" onClick={closeDrawer} />
            <aside className="bps-drawer" role="dialog" aria-modal="false" aria-labelledby="bps-drawer-title" data-testid="billing-drawer" onKeyDown={(e) => { if (e.key === 'Escape' && !confirm) closeDrawer(); }}>
              <form onSubmit={submit} noValidate aria-busy={saving ? 'true' : undefined} data-testid="billing-form">
                <header className="bps-drawer-head">
                  <h2 id="bps-drawer-title">Edit billing settings</h2>
                  <button type="button" className="bps-close" onClick={closeDrawer} aria-label="Close">×</button>
                </header>
                <div className="bps-drawer-body">
                  {summaryError ? <p className="bps-error-summary" role="alert" data-testid="billing-error-summary">{summaryError}</p> : null}
                  {fieldBlock('issuer_name', 'The name printed as the invoice issuer. Up to 120 characters.',
                    <input type="text" value={form.issuer_name} onChange={(e) => setField('issuer_name', e.target.value)} autoComplete="off" {...inputProps('issuer_name')} />)}

                  <div className="bps-field" key="issuer_address">
                    <label htmlFor="bps-issuer_address">{LABELS.issuer_address}</label>
                    {form.issuer_address.map((line, i) => (
                      <div className="bps-row" key={i}>
                        <input type="text" value={line} aria-label={`Address line ${i + 1}`} id={i === 0 ? 'bps-issuer_address' : undefined}
                          ref={i === 0 ? (el) => { firstFieldRef.current.issuer_address = el; } : undefined}
                          aria-describedby={describedBy('issuer_address')} onChange={(e) => setAddressLine(i, e.target.value)} />
                        <button type="button" className="bps-secondary" onClick={() => removeAddressLine(i)} aria-label={`Remove address line ${i + 1}`}>Remove</button>
                      </div>
                    ))}
                    <button type="button" className="bps-secondary" onClick={addAddressLine} disabled={form.issuer_address.length >= ADDRESS_MAX_LINES}>Add address line</button>
                    <p className="bps-hint" id="bps-hint-issuer_address">One line per row, at most 8.</p>
                    <p className="bps-error" id="bps-err-issuer_address" role={err('issuer_address') ? 'alert' : undefined} data-testid="billing-error-issuer_address">{err('issuer_address') || ''}</p>
                    {savedBeside('issuer_address')}
                  </div>

                  {fieldBlock('vat_number', 'Letters, numbers and spaces, 2 to 20 characters.',
                    <input type="text" value={form.vat_number} onChange={(e) => setField('vat_number', e.target.value)} autoComplete="off" {...inputProps('vat_number')} />)}
                  {fieldBlock('bank_details', 'Shown on every invoice; sensitive: saving needs your second step.',
                    <textarea rows={4} value={form.bank_details} onChange={(e) => setField('bank_details', e.target.value)} autoComplete="off" spellCheck={false} {...inputProps('bank_details')} />)}
                  {fieldBlock('payment_terms_text', 'Printed on the invoice, for example "Payment due within 14 days by bank transfer".',
                    <textarea rows={3} value={form.payment_terms_text} onChange={(e) => setField('payment_terms_text', e.target.value)} {...inputProps('payment_terms_text')} />)}

                  <div className="bps-field">
                    <label htmlFor="bps-invoice_prefix">{LABELS.invoice_prefix}</label>
                    <input type="text" value={form.invoice_prefix} onChange={(e) => setField('invoice_prefix', e.target.value)} autoComplete="off" spellCheck={false}
                      placeholder={`${context.org_code}-`} {...inputProps('invoice_prefix')} />
                    <p className="bps-next" data-testid="billing-next-number">{nextText}</p>
                    <span className="bps-sr" role="status" aria-live="polite" data-testid="billing-next-number-live">{announce}</span>
                    {numbering.first_invoice_issued ? (
                      <p className="bps-hint" data-testid="billing-prefix-locked-note">Invoices have already been issued (first: {numbering.first_invoice_number}, {numbering.issued_invoice_count} issued). Changing the prefix asks you to confirm.</p>
                    ) : null}
                    <p className="bps-hint" id="bps-hint-invoice_prefix">Letters, numbers, - or _ only; start with a letter or number; 1 to 16 characters; no spaces.</p>
                    <p className="bps-error" id="bps-err-invoice_prefix" role={err('invoice_prefix') ? 'alert' : undefined} data-testid="billing-error-invoice_prefix">{err('invoice_prefix') || ''}</p>
                    {savedBeside('invoice_prefix')}
                  </div>

                  {fieldBlock('invoice_due_days', 'Leave blank for the default of 30 days. Should match the number of days in your payment terms text.',
                    <input type="text" inputMode="numeric" value={form.invoice_due_days} onChange={(e) => setField('invoice_due_days', e.target.value)} autoComplete="off" {...inputProps('invoice_due_days')} />)}
                  <p className={`bps-live-warning${liveMismatch ? ' on' : ''}`} role="status" data-testid="billing-terms-warning">
                    {liveMismatch ? `Warning: ${liveMismatch.message}` : ''}
                  </p>

                  {fieldBlock('bookkeeper_email', 'The internal recipient of bookkeeping reports.',
                    <input type="email" value={form.bookkeeper_email} onChange={(e) => setField('bookkeeper_email', e.target.value)} autoComplete="off" {...inputProps('bookkeeper_email')} />)}
                  {fieldBlock('support_email', 'Printed on invoices as "Enquiries to:".',
                    <input type="email" value={form.support_email} onChange={(e) => setField('support_email', e.target.value)} autoComplete="off" {...inputProps('support_email')} />)}

                  <div className="bps-field" data-testid="billing-logo">
                    <label htmlFor="bps-logo">{LABELS.logo}</label>
                    <p id="bps-logo-status" data-testid="billing-logo-status">
                      {logoPending ? (logoPending.action === 'remove' ? 'The logo will be removed when you save.' : `Ready to upload: ${logoPending.name} (${Math.round(logoPending.bytes / 1024)} KB)`) : logoSentence(data.logo)}
                    </p>
                    <input id="bps-logo" ref={(el) => { firstFieldRef.current.logo = el; }} type="file" accept="image/jpeg" onChange={chooseLogo} aria-describedby="bps-logo-status bps-hint-logo bps-err-logo" />
                    {data.logo.exists && !logoConfirmRemove && !(logoPending && logoPending.action === 'remove') ? (
                      <button type="button" className="bps-secondary" onClick={() => setLogoConfirmRemove(true)}>Remove logo</button>
                    ) : null}
                    {logoConfirmRemove ? (
                      <span className="bps-inline-confirm" role="group" aria-label="Confirm logo removal">
                        Remove the logo from every invoice?{' '}
                        <button type="button" className="bps-secondary" onClick={() => { setLogoPending({ action: 'remove' }); setLogoConfirmRemove(false); }}>Yes, remove</button>{' '}
                        <button type="button" className="bps-secondary" onClick={() => setLogoConfirmRemove(false)}>Keep it</button>
                      </span>
                    ) : null}
                    <p className="bps-hint" id="bps-hint-logo">JPEG, at most 2 MB. There is no preview here: check it in Preview (PDF).</p>
                    <p className="bps-error" id="bps-err-logo" role={err('logo') ? 'alert' : undefined} data-testid="billing-error-logo">{err('logo') || ''}</p>
                  </div>
                </div>

                {confirm ? (
                  <section className="bps-confirm" role="alertdialog" aria-modal="false" aria-labelledby="bps-confirm-title" aria-describedby="bps-confirm-text" data-testid="billing-prefix-confirm"
                    onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setConfirm(null); } }}>
                    <h3 id="bps-confirm-title">Change the invoice prefix from {confirm.oldPrefix} to {confirm.newPrefix}?</h3>
                    <p id="bps-confirm-text">
                      Invoices already issued keep their numbers{confirm.firstNumber ? ` (first: ${confirm.firstNumber}, ${confirm.count} issued)` : ''}. Numbering continues from the same sequence
                      (next: {confirm.nextAfter || `${confirm.newPrefix}${padSeq(numbering.next_seq, numbering.pad_width)}`}), it does not restart at 1, and credit notes use the same prefix.
                    </p>
                    <label htmlFor="bps-confirm-reason">Reason (required)</label>
                    <textarea id="bps-confirm-reason" rows={2} value={confirm.reason} onChange={(e) => setConfirm((c) => ({ ...c, reason: e.target.value, error: '' }))} aria-describedby="bps-confirm-error" />
                    <p className="bps-error" id="bps-confirm-error" role={confirm.error ? 'alert' : undefined} data-testid="billing-confirm-error">{confirm.error}</p>
                    <div className="bps-actions">
                      <button type="button" className="bps-secondary" ref={confirmRef} onClick={() => setConfirm(null)}>Cancel</button>
                      <button type="button" className="bps-primary" onClick={confirmChange} disabled={saving}>Change prefix</button>
                    </div>
                  </section>
                ) : null}

                {stale ? (
                  <p className="bps-stale-actions"><button type="button" className="bps-secondary" data-testid="billing-reload" onClick={async () => {
                    const mine = buildChanges(form, data.profile);
                    const fresh = await readProfile();
                    if (fresh) { setForm(formWithChanges(fresh.profile, mine)); setSummaryError(STALE_SENTENCE); }
                  }}>Reload saved values</button></p>
                ) : null}

                {discardAsk ? (
                  <p className="bps-discard" role="alert" data-testid="billing-discard">
                    You have unsaved edits.{' '}
                    <button type="button" className="bps-secondary" onClick={() => { reset(); setDrawer(null); setDiscardAsk(false); }}>Discard them</button>{' '}
                    <button type="button" className="bps-secondary" onClick={() => setDiscardAsk(false)}>Keep editing</button>
                  </p>
                ) : null}

                <footer className="bps-drawer-foot">
                  <button type="button" className="bps-secondary" onClick={reset} disabled={saving || !dirty}>Reset</button>
                  <button type="submit" className="bps-primary" data-testid="billing-save" disabled={saving || hasProblem}
                    aria-describedby={hasProblem ? 'bps-save-reason' : undefined}>
                    {saving ? 'Saving...' : 'Save changes'}
                  </button>
                  {hasProblem ? <span id="bps-save-reason" className="bps-save-reason" data-testid="billing-save-reason">Fix the highlighted field to save.</span> : null}
                  <span role="status" className="bps-sr">{saving ? 'Saving...' : ''}</span>
                </footer>
              </form>
            </aside>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
